/**
 * WhatsApp Embedded Signup (0167): öffnet die Facebook-Anmeldung der Meta-App „CreatorOffice“ mit der Konfiguration für
 * WhatsApp. Die Agentur wählt oder legt ihr WhatsApp-Business-Konto und ihre Nummer an. Zurück kommen ein Code (FB.login) und
 * per postMessage von facebook.com die Kennungen von WABA und Nummer (WA_EMBEDDED_SIGNUP, Ereignis FINISH). Beides geht an die
 * Serverfunktion whatsapp-signup; der Zugang selbst erreicht den Browser nie.
 * Konfiguration aus dem Build: VITE_META_APP_ID, VITE_WHATSAPP_SIGNUP_CONFIG_ID. Fehlt eine davon, ist Verbinden nicht möglich.
 */

interface FbLoginResponse { authResponse?: { code?: string } | null; status?: string }
interface Fb {
  init(opts: Record<string, unknown>): void;
  login(cb: (r: FbLoginResponse) => void, opts: Record<string, unknown>): void;
}
declare global {
  interface Window { FB?: Fb; fbAsyncInit?: () => void }
}

const GRAPH_VERSION = "v23.0";

export function whatsappSignupConfig(): { appId: string; configId: string } | null {
  const appId = (import.meta.env.VITE_META_APP_ID as string | undefined)?.trim();
  const configId = (import.meta.env.VITE_WHATSAPP_SIGNUP_CONFIG_ID as string | undefined)?.trim();
  if (!appId || !/^\d{5,30}$/.test(appId) || !configId || !/^\d{5,30}$/.test(configId)) return null;
  return { appId, configId };
}

let sdk: Promise<Fb> | null = null;
function loadSdk(appId: string): Promise<Fb> {
  sdk ??= new Promise<Fb>((resolve, reject) => {
    if (window.FB) { window.FB.init({ appId, autoLogAppEvents: false, xfbml: false, version: GRAPH_VERSION }); resolve(window.FB); return; }
    window.fbAsyncInit = () => {
      window.FB!.init({ appId, autoLogAppEvents: false, xfbml: false, version: GRAPH_VERSION });
      resolve(window.FB!);
    };
    const s = document.createElement("script");
    s.src = "https://connect.facebook.net/en_US/sdk.js";
    s.async = true;
    s.defer = true;
    s.crossOrigin = "anonymous";
    s.onerror = () => { sdk = null; reject(new Error("sdk")); };
    document.head.appendChild(s);
  });
  return sdk;
}

/** Nachricht von facebook.com mit WABA und Nummer (Version 3 der Sitzungsdaten) */
export function readSignupMessage(origin: string, data: unknown): { waba_id: string; phone_number_id: string } | "cancel" | "error" | null {
  let host = "";
  try { host = new URL(origin).hostname; } catch { return null; }
  if (host !== "facebook.com" && !host.endsWith(".facebook.com")) return null;
  let d: { type?: unknown; event?: unknown; data?: { waba_id?: unknown; phone_number_id?: unknown } };
  try { d = typeof data === "string" ? JSON.parse(data) : (data as typeof d); } catch { return null; }
  if (!d || d.type !== "WA_EMBEDDED_SIGNUP") return null;
  // ERROR meldet Meta auch für behebbare Fehler im Dialog (z. B. „Nummer schon mit WhatsApp verknüpft“); der Dialog bleibt
  // offen und kann danach noch FINISH senden. Nur CANCEL beendet die Anmeldung (Befund 06.10.2026).
  if (d.event === "CANCEL") return "cancel";
  if (d.event === "ERROR") return "error";
  const waba = String(d.data?.waba_id ?? ""), phone = String(d.data?.phone_number_id ?? "");
  if ((d.event === "FINISH" || d.event === "FINISH_ONLY_WABA") && /^\d{5,30}$/.test(waba) && /^\d{5,30}$/.test(phone)) return { waba_id: waba, phone_number_id: phone };
  return null;
}

/** Startet die Anmeldung; löst mit Code und Kennungen auf, mit null bei Abbruch. */
export async function runEmbeddedSignup(): Promise<{ code: string; waba_id: string; phone_number_id: string } | null> {
  const cfg = whatsappSignupConfig();
  if (!cfg) throw new Error("config");
  const fb = await loadSdk(cfg.appId);
  return new Promise((resolve) => {
    let ids: { waba_id: string; phone_number_id: string } | null = null;
    let code: string | null = null;
    let done = false;
    const finish = () => {
      if (done || !code) return;
      if (!ids) return;
      done = true;
      window.removeEventListener("message", onMessage);
      resolve({ code, ...ids });
    };
    const onMessage = (e: MessageEvent) => {
      const r = readSignupMessage(e.origin, e.data);
      if (r === "cancel") { done = true; window.removeEventListener("message", onMessage); resolve(null); return; }
      if (r === "error") return;
      if (r) { ids = r; finish(); }
    };
    window.addEventListener("message", onMessage);
    fb.login((resp) => {
      code = resp.authResponse?.code ?? null;
      if (!code) { done = true; window.removeEventListener("message", onMessage); resolve(null); return; }
      // Die Nachricht mit den Kennungen kann kurz nach dem Code kommen
      finish();
      setTimeout(() => { if (!done) { done = true; window.removeEventListener("message", onMessage); resolve(null); } }, 15000);
    }, { config_id: cfg.configId, response_type: "code", override_default_response_type: true, extras: { setup: {}, featureType: "", sessionInfoVersion: "3" } });
  });
}
